import React from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Badge, Button, Modal, cn } from "../ui";
import { TierBadge } from "../TierBadge";

interface TierBenefitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTier?: number;
}

interface TierInfo {
  tier: 1 | 2 | 3 | 4;
  name: string;
  description: string;
  benefits: string[];
}

const baseTierBenefits: TierInfo[] = [
  {
    tier: 1,
    name: "Unverified",
    description: "Limited marketplace access",
    benefits: [
      "Browse listings without full addresses",
      "Create buy boxes",
      "Join the marketplace",
    ],
  },
  {
    tier: 2,
    name: "Verified",
    description: "Enhanced credibility with verified status",
    benefits: [
      "Everything above",
      "Full addresses and maps",
      "Make offers under your verified entities",
      "Verified badge",
    ],
  },
];


const TierBenefitsModal: React.FC<TierBenefitsModalProps> = ({ isOpen, onClose, currentTier }) => {
  const navigate = useNavigate();

  const tierBenefits = baseTierBenefits;
  const isVerified = (currentTier ?? 1) >= 2;

  const startVerification = () => {
    onClose();
    navigate("/profile/verification");
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Tier Benefits"
      description="Verification unlocks full addresses and making offers."
      maxWidth="max-w-2xl"
    >
      <div className={"grid grid-cols-1 gap-3 sm:grid-cols-2"}>
        {tierBenefits.map((info) => {
          const isCurrent = currentTier === info.tier;
          return (
            <div
              key={info.tier}
              className={cn(
                "flex flex-col rounded-xl p-5 ring-1 ring-inset transition-colors",
                isCurrent ? "bg-gray-50/80 ring-gray-300" : "bg-white ring-gray-200"
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <TierBadge tier={info.tier} size="small" showTooltip={false} />
                {isCurrent && (
                  <Badge tone="dark" size="sm">
                    Your Tier
                  </Badge>
                )}
              </div>
              <h3 className="mt-4 text-sm font-semibold tracking-tight text-gray-900">{info.name}</h3>
              <p className="mt-0.5 text-meta text-gray-500">{info.description}</p>

              <ul className="mt-4 space-y-2 border-t border-gray-100 pt-4">
                {info.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2 text-meta text-gray-700">
                    <Check
                      aria-hidden
                      className={cn(
                        "mt-0.5 size-3.5 shrink-0",
                        info.tier === 1 ? "text-gray-400" : "text-emerald-600"
                      )}
                      strokeWidth={2.5}
                    />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              {info.tier === 2 && (
                <div className="mt-auto pt-5">
                  {isVerified ? (
                    <Button variant="secondary" disabled block>
                      Already Verified
                    </Button>
                  ) : (
                    <Button variant="brand" block onClick={startVerification}>
                      Get Verified
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default TierBenefitsModal;
